import iconHomeAnimals from '../assets/art/icon-home-animals.svg?raw'
import { ANIMAL_IDS, type AnimalId } from '../assets/art/ids'
import { ART } from '../assets/art/map'
import { svgFromRaw } from '../assets/art/svg'
import { SFX, VOICE } from '../audio/catalog'
import { el } from '../dom'
import { createPointerLatch, isFirstPointer } from '../input/single-pointer'
import { shuffled } from './shuffle'
import type { GameModule, Unmount } from './types'

const ANIMAL_VOICE: Record<AnimalId, string> = {
  chicken: VOICE.conGa,
  duck: VOICE.conVit,
  buffalo: VOICE.conTrau,
  cat: VOICE.conMeo,
  dog: VOICE.conCho,
  pig: VOICE.conLon,
  motorbike: VOICE.xeMay,
  car: VOICE.oTo,
}

export const animalsGame: GameModule = {
  id: 'animals',
  titleVi: 'Con gì kêu?',
  iconSrc: iconHomeAnimals,
  playable: true,
  mount(root, ctx): Unmount {
    const latch = createPointerLatch()
    const order = shuffled(ANIMAL_IDS)

    const grid = el('div', {
      className: 'animal-grid',
      testId: 'game-animals',
      attrs: { 'data-feedback': 'idle' },
    })

    const tiles = order.map((id) => {
      const tile = el(
        'button',
        {
          className: 'animal-tile',
          testId: `animal-tile-${id}`,
          attrs: {
            type: 'button',
            'data-animal': id,
          },
        },
        [svgFromRaw(ART.animals[id], 'animal-tile__img')],
      )
      return tile
    })

    for (const tile of tiles) grid.append(tile)

    const playAnimal = (id: AnimalId, tile: HTMLElement) => {
      for (const other of tiles) other.classList.remove('is-pop')
      void tile.offsetWidth
      tile.classList.add('is-pop')
      grid.dataset.feedback = 'playing'
      grid.dataset.lastAnimal = id
      ctx.audio.playFeedback(SFX.tap, [ANIMAL_VOICE[id]])
    }

    const onDown = (event: PointerEvent) => {
      if (!isFirstPointer(event, latch)) return
      event.preventDefault()
      try {
        grid.setPointerCapture(event.pointerId)
      } catch {
        // ignore
      }
      const target = event.target
      if (!(target instanceof Element)) return
      const tile = target.closest('.animal-tile')
      if (!(tile instanceof HTMLElement)) return
      const id = tile.dataset.animal
      if (!isAnimalId(id)) return
      playAnimal(id, tile)
    }

    const onUp = (event: PointerEvent) => {
      latch.release(event.pointerId)
    }

    grid.addEventListener('pointerdown', onDown)
    grid.addEventListener('pointerup', onUp)
    grid.addEventListener('pointercancel', onUp)

    const scene = el('div', { className: 'animal-scene' }, [
      el('div', { className: 'animal-scene__sky', attrs: { 'aria-hidden': 'true' } }),
      el('div', { className: 'animal-scene__fence', attrs: { 'aria-hidden': 'true' } }),
      grid,
    ])
    root.append(scene)

    return () => {
      latch.reset()
      grid.removeEventListener('pointerdown', onDown)
      grid.removeEventListener('pointerup', onUp)
      grid.removeEventListener('pointercancel', onUp)
      root.replaceChildren()
    }
  },
}

function isAnimalId(value: string | undefined): value is AnimalId {
  return value !== undefined && (ANIMAL_IDS as readonly string[]).includes(value)
}
